/**
 * Contact (V2, Phase 11.5.7 — literal realDemo clone). ONE component per kind, composed from
 * the Production Foundation. This is a faithful, pixel-level port of the reference "branch"
 * section (__reference__/realDemo/landing/components/branch/view/landing-branch.tsx). The DOM
 * mirrors the reference single-branch UI EXACTLY, translating `.landing-branch*`/`.landing-row-
 * branches`/`.working-hour-*` selectors to the `.sbp-contact*` namespace (see contact.css):
 *
 *   Section (white, default tone) > Container(.sbp-contact__inner)
 *     ├─ InfoBlock (.landing-branches-filter-header-title → info-line + h2 + p)
 *     └─ .sbp-contact__branch (.landing-branch-container)
 *          ├─ iframe .sbp-contact__map (.landing-branch-map)   ← existing map mechanism preserved
 *          └─ .sbp-contact__content (.landing-branch-content)
 *               ├─ (info | hours)  ← local info/hours toggle
 *               └─ separator + .sbp-contact__today (.working-hour-container): "today" line + toggle + Actions
 *
 * Builder V2 exposes a SINGLE location (content.contact + content.location + working-hours
 * items), so the reference's multi-branch chrome is N/A and excluded (see contact.css header):
 *   - Branch selector / carousel (BranchesFilter, ArrowNext controller, list) — only rendered
 *     for >1 branch; the builder has one location, no branches[] source. EXCLUDED.
 *   - Branch/store NAME (initInfo.setting.storeName + branch.name) — sourced from app init +
 *     the branches API; no builder field. The info-header keeps its flex spacer (mirrors the
 *     reference layout) but renders no name. N/A.
 *   - Provider/contexts (LandingBranchProvider, useInitInfo, next-intl) — app-coupled, not
 *     recreated; text is literal English, business data read from the frozen contracts.
 *   - Google directions / tel: / mailto: links are the existing map mechanism (preserved).
 *
 * Public API unchanged (SectionComponentProps<'contact'>) — Registry/Renderer/Engine untouched.
 * Reads only: heading, subheading, items[] (working hours), actions, contact (phone/email),
 * location (address/geo/mapUrl). `layout` selects a CSS-only arrangement (contact.css).
 */
import { useState } from 'react'
import type { SectionComponentProps } from '../../runtime/types'
import { Section, Actions, cx } from '../shared'
import './contact.css'

export function Contact(p: SectionComponentProps<'contact'>) {
  const { content, layout, id, theme, locale } = p

  const [showHours, setShowHours] = useState(false)

  const address = content.location?.address
  const phone = content.contact?.phone
  const email = content.contact?.email
  const lat = content.location?.latitude
  const lng = content.location?.longitude
  const mapUrl = content.location?.mapUrl
  const hours = content.items ?? []
  const titleId = `${id}-title`

  // Existing map mechanism preserved: embedded Google Maps iframe from geo → address → mapUrl.
  const mapSrc =
    lat != null && lng != null
      ? `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
      : address
        ? `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`
        : (mapUrl ?? null)
  // Reference onClickDirection — google maps directions deep link (existing mechanism).
  const directions =
    lat != null && lng != null
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : address
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
        : null

  // Reference "today" row: best-effort match on the English weekday (localized labels won't match).
  const todayLong = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
  const todayShort = new Date().toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase()
  const todayItem = hours.find((h) => {
    const t = h.title.toLowerCase()
    return t === todayLong || t.startsWith(todayShort)
  })

  return (
    <Section
      as="section"
      theme={theme}
      locale={locale}
      id={id}
      ariaLabelledBy={content.heading ? titleId : undefined}
      className={cx('sbp-contact', `sbp-contact--${layout}`)}
    >
      <div className="sbp-contact__inner">
        {/* .landing-branches-filter > -header > -header-title (single branch: no controller/list) */}
        <div className="sbp-contact__filter">
          <div className="sbp-contact__filter-header">
            <div className="sbp-contact__filter-title">
              <div className="sbp-contact__line" aria-hidden="true" />
              {content.heading ? <h2 id={titleId}>{content.heading}</h2> : null}
              {content.subheading ? <p>{content.subheading}</p> : null}
            </div>
          </div>
        </div>

        {/* .landing-branch-container */}
        <div className="sbp-contact__branch">
          {mapSrc ? (
            <iframe className="sbp-contact__map" title="Location map" src={mapSrc} loading="lazy" />
          ) : (
            <div className="sbp-contact__map sbp-contact__map--empty" aria-hidden="true" />
          )}

          {/* .landing-branch-content */}
          <div className="sbp-contact__content">
            {showHours ? (
              /* BranchWorkingHours */
              <ul className="sbp-contact__hours">
                {hours.map((h) => (
                  <li
                    key={h.id}
                    className={cx(
                      'sbp-contact__hours-item',
                      todayItem && h.id === todayItem.id && 'sbp-contact__hours-item--today',
                    )}
                  >
                    <span className="sbp-contact__hours-day">{h.title}</span>
                    <span dir="ltr">{h.description}</span>
                  </li>
                ))}
              </ul>
            ) : (
              /* BranchInfo — .landing-branch-info */
              <div className="sbp-contact__info">
                {directions ? (
                  <div className="sbp-contact__info-header">
                    {/* .landing-branch-info-header > div (flex:1 spacer; store/branch name N/A) */}
                    <div />
                    <a
                      className="sbp-contact__direction"
                      href={directions}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get directions
                    </a>
                  </div>
                ) : null}
                {/* .landing-branch-info-list */}
                <address className="sbp-contact__info-list">
                  {address ? (
                    <div className="sbp-contact__info-item">
                      <h4>Address</h4>
                      <ul>
                        <li>
                          {directions ? (
                            <a
                              className="sbp-contact__info-value sbp-contact__info-value--clickable"
                              href={directions}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {address}
                            </a>
                          ) : (
                            <span className="sbp-contact__info-value">{address}</span>
                          )}
                        </li>
                      </ul>
                    </div>
                  ) : null}
                  {phone || email ? (
                    <div className="sbp-contact__info-item">
                      <h4>Contact</h4>
                      <ul>
                        {phone ? (
                          <li>
                            <a
                              className="sbp-contact__info-value sbp-contact__info-value--clickable"
                              href={`tel:${phone}`}
                            >
                              {phone}
                            </a>
                          </li>
                        ) : null}
                        {email ? (
                          <li>
                            <a
                              className="sbp-contact__info-value sbp-contact__info-value--clickable"
                              href={`mailto:${email}`}
                            >
                              {email}
                            </a>
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  ) : null}
                </address>
              </div>
            )}

            {/* working-hour "now" row */}
            <div>
              <div className="sbp-contact__sep" />
              <div className="sbp-contact__today">
                <p>
                  {todayItem ? (
                    <>
                      Today: <span dir="ltr">{todayItem.description}</span>
                    </>
                  ) : null}
                </p>
                <div className="sbp-contact__today-btns">
                  {hours.length > 0 ? (
                    <button
                      type="button"
                      className="sbp-contact__toggle"
                      onClick={() => setShowHours((s) => !s)}
                    >
                      {showHours ? 'See info' : 'See hours'}
                    </button>
                  ) : null}
                  <Actions actions={content.actions} size="md" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}
